import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import {
  coaProposalSchema,
  type CoaPlanResultRow,
  type CoaPlanWarning,
  type CoaProposal,
  type CoaProposalCommitter,
} from "@releve/shared";
import { Button } from "@/components/ui/button";
import { EditableTable, useIndexGridShell } from "@/components/ui/editable-table";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAccountSubtypes, useCommitCoaPlan } from "@/hooks/useChartOfAccounts";
import { useOrgBasePath } from "@/hooks/useOrgNavigate";
import { toast } from "@/hooks/useToast";
import type { EnvelopeError } from "@/lib/api";
import { trackEvent } from "@/lib/posthog";
import { buildCoaProposalGridColumns, type CoaProposalGridRow } from "./coaProposalGridColumns";

/**
 * `re_coa_proposal_shown` fires once per message per page load — not on every
 * remount (thread switches, the panel closing and reopening, a re-render).
 */
const shownProposalIds = new Set<string>();

interface CoaProposalCardProps {
  /** The assistant message the proposal belongs to (analytics de-dup key). */
  messageId: string;
  /** `bookkeeper_messages.coa_proposal` as read — validated here. */
  proposal: unknown;
  /** Phone surface (`/apps/bookkeeper`): touch-size buttons (docs/MOBILE.md §4.1). */
  touch?: boolean;
}

type CardState =
  | { kind: "idle" }
  | { kind: "created"; rows: CoaPlanResultRow[] }
  | { kind: "error"; message: string }
  | { kind: "dismissed" };

/** The created state, from this view's own Create or from the persisted stamp. */
interface CreatedView {
  by: CoaProposalCommitter;
  rows: CoaPlanResultRow[];
}

/** 0 for a top-level row; one step per parent level (ref chain + an existing parent). */
function depthOf(row: CoaPlanResultRow, byRef: Map<string, CoaPlanResultRow>): number {
  let depth = 0;
  let cur: CoaPlanResultRow | undefined = row;
  const seen = new Set<string>();
  while (cur && !seen.has(cur.ref)) {
    seen.add(cur.ref);
    if (cur.parent_account_id) return depth + 1;
    if (!cur.parent_ref) return depth;
    depth += 1;
    cur = byRef.get(cur.parent_ref);
  }
  return depth;
}

/**
 * Ré's chart-of-accounts proposal, under the assistant answer that made it
 * (docs/BOOKKEEPER_ASSISTANT.md — "Ré proposes; the button or a confirmed
 * reply creates"). Ré previews (`propose_accounts`, a dry run); the accounts
 * are created by this card's **Create accounts** button, or by Ré's
 * `create_accounts` after the user agreed to the card in their next message.
 * Both commit the plan STORED on this message (the button sends `message_id`)
 * and stamp it `committed`, from which the card derives its created state —
 * "by you" or "by Ré" — after a reload too. A stale hash (the chart moved
 * since the preview) is refused whole and creates nothing.
 *
 * Every name — the proposal's and a duplicate warning's — renders as plain
 * text, never markdown. Dismiss is local to this view.
 */
export function CoaProposalCard({ messageId, proposal, touch = false }: CoaProposalCardProps) {
  const parsed = useMemo(() => coaProposalSchema.safeParse(proposal), [proposal]);
  // A malformed persisted value renders no card rather than crashing the chat.
  if (!parsed.success) return null;
  return <CoaProposalCardBody messageId={messageId} proposal={parsed.data} touch={touch} />;
}

function CoaProposalCardBody({
  messageId,
  proposal,
  touch,
}: {
  messageId: string;
  proposal: CoaProposal;
  touch: boolean;
}) {
  const { t } = useTranslation("common");
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const orgBasePath = useOrgBasePath();
  const { data: subtypes } = useAccountSubtypes(orgId);
  const commit = useCommitCoaPlan();
  const [state, setState] = useState<CardState>({ kind: "idle" });

  useEffect(() => {
    if (shownProposalIds.has(messageId)) return;
    shownProposalIds.add(messageId);
    trackEvent("re_coa_proposal_shown", {
      account_count: proposal.rows.length,
      warning_count: proposal.warnings.length,
    });
  }, [messageId, proposal.rows.length, proposal.warnings.length]);

  const subtypeName = useCallback(
    (code: string | null) => (code ? subtypes?.find((s) => s.subtype_code === code)?.subtype_name ?? "" : ""),
    [subtypes],
  );

  // This view's own Create wins; otherwise the persisted stamp (a Create in
  // another view, or Ré's create_accounts) — the card never offers Create twice.
  const created: CreatedView | null =
    state.kind === "created"
      ? { by: "button", rows: state.rows }
      : proposal.committed
        ? { by: proposal.committed.by, rows: proposal.committed.rows }
        : null;

  const sourceRows = created ? created.rows : proposal.rows;
  const rows = useMemo<CoaProposalGridRow[]>(() => {
    const byRef = new Map(sourceRows.map((r) => [r.ref, r]));
    return sourceRows.map((r) => ({
      id: r.ref,
      account_number: r.account_number,
      name: r.name,
      depth: depthOf(r, byRef),
      subtype_label: subtypeName(r.subtype_code),
      account_id: r.account_id,
    }));
  }, [sourceRows, subtypeName]);

  const columns = useMemo(() => buildCoaProposalGridColumns(t, orgBasePath), [t, orgBasePath]);
  const searchHaystack = useCallback((row: CoaProposalGridRow) => [row.name], []);
  const shell = useIndexGridShell<CoaProposalGridRow>({
    rows,
    columns,
    searchHaystack,
    floatingHeader: false,
  });

  const warningText = (w: CoaPlanWarning) =>
    w.existing_account_name
      ? t("bookkeeper.coaProposal.warning", {
          name: w.name,
          existingNumber: w.existing_account_number ?? "",
          existingName: w.existing_account_name,
        })
      : t("bookkeeper.coaProposal.warningNoMatch", { name: w.name });

  const onCreate = () => {
    if (!orgId) return;
    setState({ kind: "idle" });
    commit.mutate(
      { organizationId: orgId, plan: proposal.plan, hash: proposal.hash, message_id: messageId },
      {
        onSuccess: (result) => {
          setState({ kind: "created", rows: result.rows });
          toast.success(t("bookkeeper.coaProposal.created", { count: result.rows.length }));
        },
        onError: (error) => {
          const code = (error as EnvelopeError<{ code?: string }>).envelope?.code;
          const message =
            code === "COA_PLAN_STALE"
              ? t("bookkeeper.coaProposal.stale")
              : code === "COA_PLAN_INVALID"
                ? t("bookkeeper.coaProposal.invalid")
                : code === "COA_PROPOSAL_ALREADY_COMMITTED"
                  ? t("bookkeeper.coaProposal.alreadyCreated")
                  : t("bookkeeper.coaProposal.failed", { message: error.message });
          setState({ kind: "error", message });
        },
      },
    );
  };

  if (state.kind === "dismissed") return null;

  const buttonSize = touch ? "touch" : "sm";

  return (
    <div
      className="mt-3 rounded-lg border border-border bg-card p-3 text-foreground"
      data-testid="coa-proposal-card"
    >
      <div className="mb-1 text-sm font-medium">{t("bookkeeper.coaProposal.title")}</div>
      {!created && <p className="mb-2 text-xs text-muted-foreground">{t("bookkeeper.coaProposal.intro")}</p>}

      <div className="overflow-hidden rounded-lg border">
        <EditableTable
          {...shell.tableProps}
          columns={columns}
          organizationId={orgId}
          // expandable:false → no shell action cell; hide the primitive's
          // default leading cell (it would offer Delete on a read-only list).
          hideActionColumn
        />
      </div>

      {proposal.warnings.length > 0 && !created && (
        <div className="mt-2 text-xs text-muted-foreground">
          <div className="font-medium text-foreground">{t("bookkeeper.coaProposal.warningsTitle")}</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {proposal.warnings.map((w, i) => (
              <li key={`${w.ref}-${i}`}>{warningText(w)}</li>
            ))}
          </ul>
        </div>
      )}

      {created ? (
        <div className="mt-3 flex items-center gap-1.5 text-sm" role="status">
          <Check className="h-4 w-4 shrink-0 text-primary" />
          {t(created.by === "re" ? "bookkeeper.coaProposal.createdByRe" : "bookkeeper.coaProposal.createdByYou", {
            count: created.rows.length,
          })}
        </div>
      ) : (
        <>
          {state.kind === "error" && (
            <p className="mt-2 text-xs text-destructive" role="alert">
              {state.message}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size={buttonSize} onClick={onCreate} disabled={commit.isPending || !orgId}>
              {commit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {commit.isPending ? t("bookkeeper.coaProposal.creating") : t("bookkeeper.coaProposal.create")}
            </Button>
            <Button
              size={buttonSize}
              variant="ghost"
              onClick={() => setState({ kind: "dismissed" })}
              disabled={commit.isPending}
            >
              {t("bookkeeper.coaProposal.dismiss")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
